'use client';

import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { getPlaceDetailsById } from '@/hotdesk-ui/helpers/location.helper';
import {
    loadGoogleMaps,
    preloadGoogleMaps
} from '@/hotdesk-ui/helpers/google-maps-loader';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';

export interface PlacePrediction {
    place_id: string;
    description: string;
    structured_formatting: {
        main_text: string;
        main_text_matched_substrings?: { offset: number; length: number }[];
        secondary_text?: string;
    };
}

interface UseSearchAutocompleteOptions {
    inputRef: MutableRefObject<HTMLInputElement | null>;
    onSelect: (result: GeoCodeResult) => void;
    /** When false, no predictions are fetched (e.g. modal closed). */
    enabled?: boolean;
    /**
     * Runs once Enter's pick resolves. Some callers' `onSelect` is the whole
     * action (explore re-queries in place, the header search navigates), and
     * they leave this out; others only record the place and need the second
     * step their search button does — pushing to explore, closing the modal.
     */
    onEnterSelect?: (result: GeoCodeResult) => void;
}

/**
 * Drives the custom search-suggestions dropdown. Fetches predictions from
 * Google's AutocompleteService (data, not the Autocomplete widget), tracks the
 * active row for keyboard navigation, and resolves the picked prediction to a
 * GeoCodeResult via getPlaceDetailsById before calling onSelect.
 */
export function useSearchAutocomplete({
    inputRef,
    onSelect,
    enabled = true,
    onEnterSelect
}: UseSearchAutocompleteOptions) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isOpen, setIsOpen] = useState(false);
    // Focused with nothing typed — the caller shows its empty-state panel
    // (suggested destinations) instead of predictions.
    const [isEmptyFocus, setIsEmptyFocus] = useState(false);

    const serviceRef = useRef<any>(null);
    const tokenRef = useRef<any>(null);
    // Set when we programmatically change the input after a selection (the parent
    // fills it with the picked place's address, which dispatches a synthetic
    // `input` event). We must not treat that as user typing and reopen the list.
    const suppressNextFetchRef = useRef(false);
    // What the user actually typed, kept so arrowing back above the first row
    // can put their own query back in the box.
    const typedValueRef = useRef('');

    // Latest-value refs so the native keydown listener stays stable.
    const predictionsRef = useRef(predictions);
    predictionsRef.current = predictions;
    const activeIndexRef = useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;
    // Callers pass a fresh closure every render; hold it in a ref so the native
    // keydown listener doesn't have to be torn down and rebound each time.
    const onEnterSelectRef = useRef(onEnterSelect);
    onEnterSelectRef.current = onEnterSelect;

    const fetchPredictions = useMemo(
        () =>
            debounce(async (value: string) => {
                const query = value.trim();
                if (!query) {
                    setPredictions([]);
                    setIsOpen(false);
                    return;
                }
                try {
                    await loadGoogleMaps();
                } catch {
                    return;
                }
                if (!serviceRef.current) {
                    serviceRef.current =
                        new window.google.maps.places.AutocompleteService();
                }
                if (!tokenRef.current) {
                    tokenRef.current =
                        new window.google.maps.places.AutocompleteSessionToken();
                }
                serviceRef.current.getPlacePredictions(
                    { input: query, sessionToken: tokenRef.current },
                    (preds: PlacePrediction[] | null, status: string) => {
                        if (status === 'OK' && preds && preds.length) {
                            setPredictions(preds);
                            setActiveIndex(-1);
                            setIsOpen(true);
                        } else {
                            setPredictions([]);
                            setIsOpen(false);
                        }
                    }
                );
            }, 250),
        []
    );

    useEffect(() => {
        return () => fetchPredictions.cancel();
    }, [fetchPredictions]);

    /**
     * Give up focus once a place is picked. Rows select on `mousedown`
     * with `preventDefault()` so the pick lands before the input's blur, which
     * leaves the input focused afterwards — on mobile that keeps the keyboard
     * up, and it defeats the `activeElement` guard below, so the programmatic
     * refills that follow a selection read as typing and reopen the list.
     */
    const blurInput = useCallback(() => {
        inputRef.current?.blur();
    }, [inputRef]);

    const close = useCallback(() => {
        setIsOpen(false);
        setIsEmptyFocus(false);
        blurInput();
    }, [blurInput]);

    /**
     * Back to the "focused, nothing typed" state — for the clear (X) button.
     * Wiping `input.value` in code fires no `input` event, so without this the
     * predictions from the erased query would stay on screen.
     */
    const resetToEmpty = useCallback(() => {
        fetchPredictions.cancel();
        setPredictions([]);
        setActiveIndex(-1);
        setIsOpen(false);
        setIsEmptyFocus(true);
        typedValueRef.current = '';
    }, [fetchPredictions]);

    /**
     * Show the row the keyboard is on in the input itself, the way a browser's
     * address bar does — index -1 means "off the list", so the query the user
     * typed goes back. Only the arrow keys call this: `activeIndex` also moves
     * on mouse hover, and rewriting the box as the pointer crosses the list
     * would be maddening.
     *
     * Assigning `value` fires no `input` event, so this can't be mistaken for
     * typing or kick off another fetch.
     */
    const previewIndex = useCallback(
        (index: number) => {
            const input = inputRef.current;
            if (!input) return;
            const next =
                (index >= 0
                    ? predictionsRef.current[index]?.description
                    : typedValueRef.current) ?? typedValueRef.current;
            if (input.value === next) return;
            input.value = next;
            // Land the caret at the end rather than wherever it was in the
            // shorter/longer string it replaced.
            input.setSelectionRange(next.length, next.length);
        },
        [inputRef]
    );

    const selectByIndex = useCallback(
        (index: number, afterSelect?: (result: GeoCodeResult) => void) => {
            const prediction = predictionsRef.current[index];
            if (!prediction) return;
            if (inputRef.current) {
                inputRef.current.value = prediction.description;
            }
            setPredictions([]);
            setActiveIndex(-1);
            setIsOpen(false);
            setIsEmptyFocus(false);
            blurInput();
            // Drop an in-flight debounced fetch for the half-typed query, which
            // would otherwise land after this and reopen the list.
            fetchPredictions.cancel();
            // The parent will refill the input with the resolved address, which
            // fires a synthetic `input` event — don't let it reopen the list.
            suppressNextFetchRef.current = true;
            // A place selection ends the autocomplete session.
            tokenRef.current = null;
            getPlaceDetailsById(prediction.place_id, (result) => {
                onSelect(result);
                afterSelect?.(result);
            });
        },
        [inputRef, onSelect, blurInput, fetchPredictions]
    );

    // Used by the search button: if the user typed but didn't pick a suggestion,
    // resolve the highlighted row (or the first one) as if they had clicked it.
    // Returns true if a suggestion was chosen (so the caller can skip its default
    // search). `afterSelect` runs once the place details resolve.
    const selectActiveOrFirst = useCallback(
        (afterSelect?: (result: GeoCodeResult) => void): boolean => {
            if (!predictionsRef.current.length) return false;
            const idx =
                activeIndexRef.current >= 0 ? activeIndexRef.current : 0;
            selectByIndex(idx, afterSelect);
            return true;
        },
        [selectByIndex]
    );

    // Input / keyboard listeners bound to the underlying <input>.
    useEffect(() => {
        const input = inputRef.current;
        if (!input || !enabled) return;

        const handleInput = () => {
            // Only react to genuine user typing (input focused). Programmatic
            // prefills — reflecting the resolved location on page load, or the
            // parent refilling the box after a pick — fire an input event too,
            // but must not open the suggestions list. A selection blurs the
            // input, so every refill it triggers lands here unfocused, however
            // many times the parent's effect re-runs.
            if (document.activeElement !== input) {
                // Don't let the guard outlive the refill it was armed for and
                // swallow the user's next keystroke.
                suppressNextFetchRef.current = false;
                return;
            }
            // A refill that somehow arrives while still focused.
            if (suppressNextFetchRef.current) {
                suppressNextFetchRef.current = false;
                return;
            }
            typedValueRef.current = input.value;
            setIsEmptyFocus(!input.value.trim());
            fetchPredictions(input.value);
        };
        const handleFocus = () => {
            // Maps is loaded on demand; start now so it's ready by the
            // first debounced keystroke.
            preloadGoogleMaps();
            if (!input.value.trim()) {
                setIsEmptyFocus(true);
                return;
            }
            setIsEmptyFocus(false);
            if (predictionsRef.current.length) {
                setIsOpen(true);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsEmptyFocus(false);
            }
            if (!isOpenRef.current || !predictionsRef.current.length) return;
            // The arrows move the highlight and mirror it into the input, so
            // the next index is computed here rather than in a state updater —
            // `previewIndex` needs it now, and writing the ref keeps a burst of
            // keypresses moving one row at a time instead of all reading the
            // same pre-render value.
            const moveTo = (index: number) => {
                activeIndexRef.current = index;
                setActiveIndex(index);
                previewIndex(index);
            };

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                moveTo(
                    Math.min(
                        activeIndexRef.current + 1,
                        predictionsRef.current.length - 1
                    )
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                // -1 is a real stop, not a clamp: it hands the box back to what
                // the user typed.
                moveTo(Math.max(activeIndexRef.current - 1, -1));
            } else if (e.key === 'Enter') {
                // With the list open, Enter always commits — the highlighted
                // row, or the first one when the user typed and never arrowed —
                // and lands the caller in the same place its search button
                // would have.
                e.preventDefault();
                selectActiveOrFirst(onEnterSelectRef.current);
            } else if (e.key === 'Escape') {
                // Dismiss suggestions without letting an enclosing modal also
                // handle Escape (it should close on a second press). Anything
                // an arrow key previewed into the box is undone with it.
                e.stopPropagation();
                moveTo(-1);
                setIsOpen(false);
            }
        };

        input.addEventListener('input', handleInput);
        input.addEventListener('focus', handleFocus);
        input.addEventListener('keydown', handleKeyDown);
        return () => {
            input.removeEventListener('input', handleInput);
            input.removeEventListener('focus', handleFocus);
            input.removeEventListener('keydown', handleKeyDown);
        };
    }, [inputRef, enabled, fetchPredictions, selectActiveOrFirst, previewIndex]);

    // Close on outside click.
    useEffect(() => {
        const handleDocMouseDown = (e: MouseEvent) => {
            const target = e.target as Node | null;
            // A control that removes itself as part of its own click — e.g.
            // "Clear" wiping the recent-searches list — is already detached by
            // the time this bubbles to the document, and `contains()` reports
            // a detached node as outside. Ignore those so the panel stays open.
            if (target && !document.contains(target)) return;

            if (containerRef.current && !containerRef.current.contains(target)) {
                setIsOpen(false);
                setIsEmptyFocus(false);
            }
        };
        document.addEventListener('mousedown', handleDocMouseDown);
        return () =>
            document.removeEventListener('mousedown', handleDocMouseDown);
    }, []);

    return {
        containerRef,
        predictions,
        activeIndex,
        isOpen,
        isEmptyFocus,
        setActiveIndex,
        selectByIndex,
        selectActiveOrFirst,
        close,
        resetToEmpty
    };
}
